import { ChargingSessionModel } from '../../models/charging-sessions.model'
import { ChargingSessionDto } from '../dto/charging-sessions.dto'
import { TariffCategory } from 'src/Libs/e-mobility/shared/models/types'

export const chargingSessionTransformer = (response: ChargingSessionDto): ChargingSessionModel => {
  const { session } = response

  return {
    id: session.id,
    authentication: {
      tokenType: session.cdr_token.type,
      rfid: response.emobilityToken ?? undefined,
      cardName: response.tokenName ?? undefined,
      ocpiTokenId: response.session.cdr_token.uid,
      authenticationMethod: response.authType ?? 'NONE',
      isAuthenticated: (() => {
        return Boolean(response.emobilityToken)
      })(),
    },
    wallbox: {
      name: response.wallboxName ?? '',
      id: response.wallboxId,
      type: response.wallboxType,
    },
    status: (() => {
      if (!response.status) {
        return 'NULL'
      }

      /**
       * Due to limitations in the E3/DC storage hardware, there's a delay between successful card authentication
       * and receiving a session status update indicating that charging has started and the session is in one of the active
       * states.
       *
       * This causes a mismatch in the frontend: we receive authentication data, but the status still says "WAIT_AUTHENTICATION".
       * To avoid confusing the user by showing "Waiting for authentication" while authentication data is already present,
       * we derive a new frontend-only status: "WAITING_AFTER_SUCCESSFUL_AUTHENTICATION".
       *
       * The condition is: we find real auth data in the response together with the "WAIT_AUTHENTICATION" status and override it in
       * the transformer.
       */
      const authDataReceived = Boolean(response.emobilityToken)

      if (authDataReceived && response.status === 'WAIT_AUTHENTICATION') {
        return 'WAITING_AFTER_SUCCESSFUL_AUTHENTICATION'
      }

      /** For all other cases, we simply return what was sent by BE. */
      return response.status
    })(),
    solarEnergyRatio: response.solarEnergyRatio,
    durationMilliseconds: response.duration,
    totalCost: (() => {
      const totalCost = session.total_cost

      if (!totalCost) {
        return null
      }

      return totalCost.incl_vat
    })(),
    startDateTime: session.start_date_time,
    cdrToken: session.cdr_token,
    chargingPeriods:
      session.charging_periods?.map(chargingPeriod => {
        const { start_date_time, tariff_id, dimensions } = chargingPeriod

        return {
          startDateTime: start_date_time,
          tariffId: tariff_id,
          dimensions: dimensions,
        }
      }) || [],
    dischargePrice: response.dischargePrice,
    connectorId: session.connector_id,
    gridEnergyKWh: session.kwh - response.energySolar,
    consumptionKWh: session.kwh,
    countryCode: session.country_code,
    currencyCode: session.currency,
    endDateTime: (() => {
      if (!session.end_date_time) {
        /** Catch empty string, undefined or null in the DTO, but set the value to undefined in FE model for consistency */
        return undefined
      }

      /**
       * For some ongoing sessions, E3/DC returns an invalid(or, to be more precise, "default") value in the "the end_date_time"
       * field. This default value is the UNIX zero time of 1970-01-01T00:00:00 and it stays like this until the session finishes,
       * overwriting the default timestamp with the actual time when the session completed.
       *
       * To avoid showing this incorrect 1970 timestamp in the FE, we catch it here in the transformer and set it in the
       * FE model as undefined - as if it was returned empty from the BE.
       *
       * In case E3/DC backend service patches this behavior, the next two lines can be omitted.
       */
      const isEndDateInvalid = session.end_date_time < session.start_date_time

      if (isEndDateInvalid) {
        return undefined
      }

      return session.end_date_time
    })(),
    evseUid: session.evse_uid,
    sessionId: session.id,
    lastUpdated: session.lastUpdated,
    locationId: session.location_id,
    meterId: session.meter_id ?? '',
    partyId: session.party_id,
    energySolarKWh: response.energySolar,
    energyDischargedKWh: response.energyDischarged,
    dischargeTimeActiveMilliseconds: response.dischargeTimeActive,
    dischargeCounterReadingStart: response.dischargeCounterReadingStart,
    dischargeCounterReadingStop: response.dischargeCounterReadingStop,
    totalDischargeCost: response.totalDischargeCost ?? 0,
    chargeTimeActiveMilliseconds: response.chargeTimeActive,
    chargeTimeInactiveMilliseconds: response.chargeTimeInactive,
    tariffCategory: response.tariffCategory ?? TariffCategory.staticTariff,
    cdrId: response.cdrId,
    midInfo: {
      midMeterConnected: response.midEnergy,
      midEnergyInconsistent: response.midEnergyInconsistent,
      midSessionOffline: response.midSessionOffline,
    },
    chargePrice: response.chargePrice,
    solarPrice: response.solarPrice,
    gridPrice: response.gridPrice,
    isCostFinal: response.status === 'FINISHED' || response.status === 'FINISHED_NO_ACTION',
    gridCost: response.gridCost,
    solarCost: response.solarCost,
    emobilitySessionId: response.emobilitySessionId,
  }
}
